import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { compact, find, every, stubFalse } from "lodash/fp";

import useDateRangeStore from "@app/analytics/stores/useDateRangeStore";
import {
  getDateRanges,
  getQueryKeyFromDateRanges,
  getRoundedValues,
} from "@app/analytics/utils";
import { getCoachingData } from "@app/common/api/apiClient/coaching";
import { DEFAULT_COACHING_DATA } from "@app/common/api/apiClient/parsers/coaching";
import { useAuthenticated } from "@app/common/auth/useAuth";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import {
  AggregateCoachingData,
  AggregateCoachingItemData,
  BaseAggregateCoachingData,
  BaseAggregateCoachingItemData,
  BaseCoachingSession,
} from "@app/common/types";
import {
  getFillerWordsSemanticScore,
  getInterruptionsSemanticScore,
  getWpmSemanticScore,
  isWpmWithinRecommendedRange,
  getQuestionsAskedSemanticScore,
  getNonInclusiveTermsSemanticScore,
} from "@app/common/utils/coachingUtils";

import useCoachingSessions, { SessionMap } from "./useCoachingSessions";

type SessionFilterFunc = (session: BaseCoachingSession) => boolean;

const populateSessionData = (
  coachingData: BaseAggregateCoachingItemData,
  sessionData: SessionMap,
  {
    secondaryData,
    sessionFilterFunc = stubFalse,
  }: {
    secondaryData?: BaseAggregateCoachingItemData;
    sessionFilterFunc?: SessionFilterFunc;
  } = {}
): Pick<AggregateCoachingItemData, "average" | "sessions" | "moments"> => {
  const mergedSessions = coachingData.sessions.map((session) => {
    if (sessionFilterFunc(session)) return;

    const dataForSession = sessionData.get(session.sessionId);
    if (!dataForSession) return;

    const secondarySession = find(
      { sessionId: session.sessionId },
      secondaryData?.sessions ?? []
    );

    return {
      ...session,
      ...dataForSession,
      secondaryRate: secondarySession?.rate,
    };
  });

  const mergedMoments = coachingData.moments.map((moment) => {
    const sessionForMoment = sessionData.get(moment.sessionId)!;
    return {
      ...moment,
      session: sessionForMoment,
    };
  });

  const rawAverage = sessionData.size
    ? (coachingData.count ?? 0) / sessionData.size
    : null;

  return {
    average: getRoundedValues(rawAverage),
    sessions: compact(mergedSessions),
    moments: mergedMoments,
  };
};

const excludeWpmSession = (session: BaseCoachingSession) =>
  isWpmWithinRecommendedRange(session.rate);

const combineCoachingAndSessionsData = (
  coachingData: BaseAggregateCoachingData,
  sessionData: SessionMap
): AggregateCoachingData => {
  const fillerWords = {
    ...coachingData.fillerWords,
    ...populateSessionData(coachingData.fillerWords, sessionData),
    semanticScore: getFillerWordsSemanticScore(coachingData.fillerWords.rate),
  };

  const interruptions = {
    ...coachingData.interruptions,
    ...populateSessionData(coachingData.interruptions, sessionData),
    semanticScore: getInterruptionsSemanticScore(
      coachingData.interruptions.rate
    ),
  };

  const { average: termsPerMeeting, ...termsData } = populateSessionData(
    coachingData.nonInclusiveTerms,
    sessionData
  );

  const nonInclusiveTerms = {
    ...coachingData.nonInclusiveTerms,
    ...termsData,
    average: termsPerMeeting,
    semanticScore: getNonInclusiveTermsSemanticScore(termsPerMeeting.value),
  };

  const { average: questionsPerMeeting, ...questionsData } =
    populateSessionData(coachingData.nonInclusiveTerms, sessionData);

  const questions = {
    ...coachingData.questions,
    ...questionsData,
    average: questionsPerMeeting,
    semanticScore: getQuestionsAskedSemanticScore(questionsPerMeeting.value),
  };

  const talkingPace = {
    ...coachingData.talkingPace,
    ...populateSessionData(coachingData.talkingPace, sessionData, {
      secondaryData: coachingData.fillerWords,
      sessionFilterFunc: excludeWpmSession,
    }),
    semanticScore: getWpmSemanticScore(coachingData.talkingPace.rate),
  };

  return {
    fillerWords,
    interruptions,
    nonInclusiveTerms,
    questions,
    talkingPace,
  };
};

const useCoachingDashboardData = () => {
  const { user } = useAuthenticated();
  const { canAccessCoaching } = useFeatureAccess();

  const dateRanges = useDateRangeStore(getDateRanges);

  const dateRangeQueryKey = getQueryKeyFromDateRanges(dateRanges);

  const coachingQuery = useQuery({
    queryKey: ["coaching", "aggregates", user.id, dateRangeQueryKey],
    queryFn: () => getCoachingData(dateRanges[0]),
    enabled: canAccessCoaching,
    refetchOnWindowFocus: false,
  });

  const { sessions, ...sessionsQuery } = useCoachingSessions({
    dateRanges,
    userId: user.id,
    enabled: canAccessCoaching,
  });

  const rawCoachingData = coachingQuery.data ?? DEFAULT_COACHING_DATA;

  const isInitialLoading =
    coachingQuery.isInitialLoading || sessionsQuery.isLoading;
  const isError = coachingQuery.isError || sessionsQuery.isError;

  const coachingData = useMemo(
    () => combineCoachingAndSessionsData(rawCoachingData, sessions),
    [rawCoachingData, sessions, dateRangeQueryKey]
  );

  const refetch = () => {
    if (coachingQuery.isError) coachingQuery.refetch();
    if (sessionsQuery.isError) sessionsQuery.refetch();
  };

  const noData =
    !isInitialLoading &&
    (sessions.size < 1 ||
      every(
        (d) => d.rate == null && d.average == null,
        Object.values(coachingData)
      ));

  return {
    isInitialLoading,
    isError,
    noData,
    sessionCount: sessions.size,
    refetch,
    ...coachingData,
  };
};

export default useCoachingDashboardData;
